import React, { useState, useEffect } from "react";
import { useCustomer } from "../services/hooks/useCustomer";
import { useGlobal } from "../services/hooks/useGlobal";
import Rating from "./Rating";
import time from "../services/time/time";
import { useTimezone } from "../services/hooks/useTimezone";
import ReactPlaceHolder from "react-placeholder";
import Notice from "./Notice";
import Avatar from "./Avatar";
import { enableTooltip } from "../public/js/core";
import { useReviewVote } from "../services/hooks/useReviewVote";
import { useSnackbar } from "../services/hooks/useSnackbar";
import { useAuth } from "../services/hooks/useAuth";
import { useRequireLogin } from "../services/hooks/useRequireLogin";

const Review = React.memo(
  ({ id, userId, rating, created, title, content, upVotes, downVotes }) => {
    const { user } = useAuth();

    const { fetchCustomer, customer, customerError, customerStatus } =
      useCustomer();

    const { timezone } = useTimezone();

    const { requireLogin } = useRequireLogin();

    const { setReviewIdToReport } = useGlobal();

    const {
      upVoteReview,
      reviewUpVote,
      downVoteReview,
      reviewDownVote,
      reviewUpVoteError,
      reviewDownVoteError,
      isUpVoting,
      isDownVoting,
    } = useReviewVote();

    const { addSnackbar } = useSnackbar();

    const [isUpVoted, setIsUpVoted] = useState(false);

    const [isDownVoted, setIsDownVoted] = useState(false);

    const [shouldIncrementUpVotes, setShouldIncrementUpVotes] = useState(false);
    const [shouldIncrementDownVotes, setShouldIncrementDownVotes] =
      useState(false);

    const [shouldDecrementUpVotes, setShouldDecrementUpVotes] = useState(false);
    const [shouldDecrementDownVotes, setShouldDecrementDownVotes] =
      useState(false);

    const handleUpVote = (e) => {
      e.preventDefault();

      upVoteReview(id);
    };

    const handleDownVote = (e) => {
      e.preventDefault();

      downVoteReview(id);
    };

    const handleReportReview = (e) => {
      if (e) {
        e.preventDefault();
      }

      setReviewIdToReport({
        reviewIdToReport: id,
      });
    };

    useEffect(() => {
      if (customerStatus === "ready") {
        enableTooltip();
      }
    }, [customerStatus]);

    useEffect(() => {
      fetchCustomer(userId);
    }, [userId]);

    useEffect(() => {
      setShouldDecrementUpVotes(
        upVotes.some((upVote) => upVote.userId === user?.id) && isDownVoted
      );
    }, [upVotes, isDownVoted]);

    useEffect(() => {
      setShouldDecrementDownVotes(
        downVotes.some((downVote) => downVote.userId === user?.id) && isUpVoted
      );
    }, [downVotes, isUpVoted]);

    useEffect(() => {
      setShouldIncrementUpVotes(
        upVotes.every((upVote) => upVote.userId !== user?.id) && isUpVoted
      );
    }, [upVotes, isUpVoted]);

    useEffect(() => {
      setShouldIncrementDownVotes(
        downVotes.every((downVote) => downVote.userId !== user?.id) &&
          isDownVoted
      );
    }, [downVotes, isDownVoted]);

    useEffect(() => {
      setIsUpVoted(upVotes.some((upVote) => upVote.userId === user?.id));
    }, [upVotes]);

    useEffect(() => {
      setIsDownVoted(
        downVotes.some((downVote) => downVote.userId === user?.id)
      );
    }, [downVotes]);

    useEffect(() => {
      if (reviewUpVote) {
        setIsUpVoted(true);
        setIsDownVoted(false);

        addSnackbar("Thanks for your feedback.", {
          type: "success",
        });
      }
    }, [reviewUpVote]);

    useEffect(() => {
      if (reviewDownVote) {
        setIsUpVoted(false);
        setIsDownVoted(true);

        addSnackbar("Thanks for your feedback.", {
          type: "success",
          callToAction: "Report Review",
          callToActionFn: handleReportReview,
          dismissAfter: 3000,
        });
      }
    }, [reviewDownVote]);

    useEffect(() => {
      if (reviewUpVoteError) {
        addSnackbar(reviewUpVoteError, {
          type: "danger",
        });
      }
    }, [reviewUpVoteError]);

    useEffect(() => {
      if (reviewDownVoteError) {
        addSnackbar(reviewDownVoteError, {
          type: "danger",
        });
      }
    }, [reviewDownVoteError]);

    return (
      <>
        {/* Review */}
        <div className="review">
          <ReactPlaceHolder
            type="media"
            ready={customerStatus === "ready"}
            rows={8}
            style={{ marginTop: "2rem" }}
          >
            {customerError ? (
              <Notice
                message="An error occurred while trying to display this review. Please refresh the page or contact support if the problem persists."
                type="danger"
                dismissable={false}
              />
            ) : (
              <>
                {/* Body */}
                <div className="review-body">
                  <div className="row">
                    <div className="col-12 col-md-auto">
                      {/* Avatar */}

                      <Avatar
                        url={customer?.avatar?.src}
                        initials={
                          customer?.username?.charAt(0).toUpperCase() ??
                          `${customer?.firstName
                            .charAt(0)
                            .toUpperCase()} ${customer?.lastName
                            .charAt(0)
                            .toUpperCase()}`
                        }
                        loading={!customer}
                      />
                    </div>
                    <div className="col-12 col-md">
                      {/* Header */}
                      <div className="row mb-6">
                        <div className="col-12">
                          {/* Rating */}
                          <Rating rating={rating} size="sm" />
                        </div>
                        <div className="col-12">
                          {/* Time */}
                          <span className="font-size-xs text-muted">
                            {customer
                              ? customer.username
                                ? customer.username
                                : `${
                                    customer?.firstName
                                  } ${customer?.lastName?.charAt(0)}.`
                              : ""}{" "}
                            -{" "}
                            <time
                              dateTime={time
                                .unix(created)
                                .tz(timezone)
                                .format()}
                            >
                              {time
                                .unix(created)
                                .tz(timezone)
                                .format("DD MMM YYYY")}
                            </time>
                          </span>
                        </div>
                      </div>
                      {/* Title */}
                      <p className="mb-2 font-size-lg font-weight-bold">
                        {title}
                      </p>
                      {/* Text */}
                      <p className="text-gray-500">{content}</p>
                      {/* Footer */}
                      <div className="row align-items-center">
                        <div className="col-auto d-none d-lg-block">
                          {/* Text */}
                          <p className="mb-0 font-size-sm">
                            Was this review helpful?
                          </p>
                        </div>
                        <div className="col-auto mr-auto">
                          {/* Rate */}
                          <div className="rate">
                            <a
                              className="rate-item"
                              data-toggle="tooltip"
                              title="Helpful"
                              data-count={
                                shouldIncrementUpVotes
                                  ? upVotes.length + 1
                                  : shouldDecrementUpVotes
                                  ? upVotes.length - 1
                                  : upVotes.length
                              }
                              href="#"
                              role="button"
                              onClick={user ? handleUpVote : requireLogin}
                            >
                              {isUpVoting ? (
                                <i class="fas fa-circle-notch fa-spin"></i>
                              ) : isUpVoted ? (
                                <i className="fa fa-thumbs-up" />
                              ) : (
                                <i className="far fa-thumbs-up" />
                              )}
                            </a>
                            <a
                              className="rate-item"
                              data-toggle="tooltip"
                              title="Unhelpful"
                              data-count={
                                shouldIncrementDownVotes
                                  ? downVotes.length + 1
                                  : shouldDecrementDownVotes
                                  ? downVotes.length - 1
                                  : downVotes.length
                              }
                              href="#"
                              role="button"
                              onClick={user ? handleDownVote : requireLogin}
                            >
                              {isDownVoting ? (
                                <i class="fas fa-circle-notch fa-spin"></i>
                              ) : isDownVoted ? (
                                <i className="fa fa-thumbs-down" />
                              ) : (
                                <i className="far fa-thumbs-down" />
                              )}
                            </a>
                          </div>
                        </div>
                        <div className="col-auto">
                          <a
                            className="rate-item"
                            data-toggle="tooltip"
                            title="Report"
                            href="#"
                            role="button"
                            onClick={user ? handleReportReview : requireLogin}
                          >
                            <i className="far fa-flag" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </ReactPlaceHolder>
        </div>
      </>
    );
  }
);

export default Review;
